/**
 * Time-input parsing + pace formatting for the Training page.
 *
 * Race times are entered as H:MM:SS / MM:SS; the API speaks whole seconds.
 * Target paces come back as seconds-per-kilometre and are shown in BOTH
 * min:sec/km and min:sec/mi so the runner can read whichever they think in.
 */

import type { UnitPref } from './units.js';

const MILE_KM = 1.609344;

/**
 * Parse an "H:MM:SS", "MM:SS", or "M:SS" clock string to seconds.
 * Lenient about leading/trailing whitespace; each part must be numeric.
 * Returns null for empty or malformed input (so the form can flag it).
 */
export function parseHMS(input: string): number | null {
  const raw = input.trim();
  if (!raw) return null;
  const parts = raw.split(':');
  if (parts.length < 1 || parts.length > 3) return null;
  let seconds = 0;
  for (const part of parts) {
    if (!/^\d+$/.test(part.trim())) return null;
    seconds = seconds * 60 + Number(part);
  }
  return seconds;
}

/** Seconds -> "H:MM:SS" (drops the hour when zero: "M:SS"). */
export function fmtHMS(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number): string => (n < 10 ? '0' : '') + n;
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

/** Seconds -> "M:SS" (used for per-unit paces where hours never occur). */
export function fmtMinSec(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
}

/** sec/km -> "M:SS/km". */
export function fmtPaceKm(secPerKm: number | null | undefined): string {
  if (secPerKm == null || !Number.isFinite(secPerKm) || secPerKm <= 0) return '—';
  return `${fmtMinSec(secPerKm)}/km`;
}

/** sec/km -> "M:SS/mi". */
export function fmtPaceMi(secPerKm: number | null | undefined): string {
  if (secPerKm == null || !Number.isFinite(secPerKm) || secPerKm <= 0) return '—';
  return `${fmtMinSec(secPerKm * MILE_KM)}/mi`;
}

/** Friendly dual-unit pace, e.g. "5:00/km · 8:03/mi". */
export function fmtPaceBoth(secPerKm: number | null | undefined): string {
  if (secPerKm == null || !Number.isFinite(secPerKm) || secPerKm <= 0) return '—';
  return `${fmtPaceKm(secPerKm)} · ${fmtPaceMi(secPerKm)}`;
}

/**
 * Dual-unit pace with the preferred unit FIRST: imperial "8:03/mi · 5:00/km",
 * metric "5:00/km · 8:03/mi". The single (imperial) user reads min/mi primary.
 */
export function fmtPacePref(secPerKm: number | null | undefined, pref: UnitPref = 'imperial'): string {
  if (secPerKm == null || !Number.isFinite(secPerKm) || secPerKm <= 0) return '—';
  return pref === 'imperial'
    ? `${fmtPaceMi(secPerKm)} · ${fmtPaceKm(secPerKm)}`
    : `${fmtPaceKm(secPerKm)} · ${fmtPaceMi(secPerKm)}`;
}

/** A plan distance stored in km → "3.1 mi" (imperial) or "5.0 km" (metric). */
export function fmtDistanceKm(km: number | null | undefined, pref: UnitPref = 'imperial'): string {
  if (km == null || !Number.isFinite(km)) return '—';
  return pref === 'imperial' ? `${(km / MILE_KM).toFixed(1)} mi` : `${km.toFixed(1)} km`;
}
